/**
 * Demo Data Service
 * Fetches REAL seeded data from the database for demo mode
 * Falls back to mock data if database is unreachable
 * This ensures demo mode shows the same data that's actually in the database
 */

import { apiClient } from '@/lib/api-client';

/**
 * Mock financial data for offline demo mode
 * Used as fallback when database is unreachable
 */
const MOCK_DEMO_DATA_BASE = {
  cashPosition: {
    currentBalance: 487234,
    totalReceivables: 842150,
    totalPayables: 354916
  },
  receivables: {
    total: 842150,
    overdue: 187430,
    aging: {
      current: 485720,
      thirtyDays: 169000,
      sixtyDays: 127000,
      // BUG-113: the old ninetyPlus of 60430 meant ">60 days". Split across the
      // two real bands so the fallback shape matches the live shape and the
      // total is unchanged.
      ninetyDays: 30430,
      ninetyPlus: 30000
    },
    topInvoices: [
      {
        id: 'mock-inv-1',
        buyerName: 'Acme Manufacturing',
        invoiceNumber: 'INV-2024-1523',
        amount: 89450,
        dueDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        daysOverdue: 45
      },
      {
        id: 'mock-inv-2',
        buyerName: 'TechCorp Solutions',
        invoiceNumber: 'INV-2024-1489',
        amount: 67200,
        dueDate: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString(),
        daysOverdue: 32
      },
      {
        id: 'mock-inv-3',
        buyerName: 'Global Distributors',
        invoiceNumber: 'INV-2024-1401',
        amount: 30780,
        dueDate: new Date(Date.now() - 67 * 24 * 60 * 60 * 1000).toISOString(),
        daysOverdue: 67
      }
    ]
  },
  payables: {
    total: 354916,
    dueThisWeek: 89400,
    dueSoon: 123560,
    topBills: [
      {
        id: 'mock-bill-1',
        supplierName: 'SupplyCo Industries',
        billNumber: 'BILL-2024-7892',
        amount: 42100,
        dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: 'mock-bill-2',
        supplierName: 'TechVendor LLC',
        billNumber: 'BILL-2024-7856',
        amount: 18900,
        dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: 'mock-bill-3',
        supplierName: 'GlobalParts Inc',
        billNumber: 'BILL-2024-7823',
        amount: 67400,
        dueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString()
      }
    ],
    bills: [
      { id: 'mock-bill-1', billNumber: 'BILL-2024-7892', supplierName: 'SupplyCo Industries', amount: 42100, dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), status: 'pending', approvalStatus: 'pending' },
      { id: 'mock-bill-2', billNumber: 'BILL-2024-7856', supplierName: 'TechVendor LLC', amount: 18900, dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), status: 'pending', approvalStatus: 'pending' },
      { id: 'mock-bill-3', billNumber: 'BILL-2024-7823', supplierName: 'GlobalParts Inc', amount: 67400, dueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(), status: 'approved', approvalStatus: 'approved' }
    ]
  },
  company: {
    name: 'Summit Manufacturing Inc.',
    id: 'c6139b73-7213-4662-9fa1-17866c87e486'
  }
};

export interface CashSeriesPoint {
  /** Period label shown on the X axis */
  period: string;
  /** Cash position at the END of the period (anchored to currentBalance today) */
  position: number;
  /** Sum of completed 'received' payments inside the period */
  moneyIn: number;
  /** Sum of completed 'sent' payments inside the period */
  moneyOut: number;
}

export interface DemoPayment {
  id: string;
  referenceNumber: string;
  date: string; // YYYY-MM-DD
  amount: number;
  type: 'received' | 'sent';
  status: string;
  description: string;
}

export interface CashPositionSeries {
  days: CashSeriesPoint[];
  weeks: CashSeriesPoint[];
  months: CashSeriesPoint[];
  quarters: CashSeriesPoint[];
  years: CashSeriesPoint[];
}

export interface DemoFinancialData {
  cashPosition: {
    currentBalance: number;
    totalReceivables: number;
    totalPayables: number;
  };
  /** Seeded payment records + the chart series derived from them (BUG-24) */
  cashFlow: {
    payments: DemoPayment[];
    series: CashPositionSeries;
  };
  /** Real record counts from the seeded dataset (FR-21 interim: sync-step counts) */
  counts: {
    invoices: number;
    bills: number;
    payments: number;
  };
  receivables: {
    total: number;
    overdue: number;
    /**
     * The standard aging ladder. Each key is exactly the band its name says
     * (BUG-113) — `ninetyDays` is 61-90, `ninetyPlus` is 91 and over. Before
     * that fix there was no 61-90 band and `ninetyPlus` silently meant ">60".
     */
    aging: {
      current: number;
      thirtyDays: number;
      sixtyDays: number;
      ninetyDays: number;
      ninetyPlus: number;
    };
    topInvoices: Array<{
      id: string;
      buyerName: string;
      invoiceNumber: string;
      amount: number;
      dueDate: string;
      daysOverdue: number;
    }>;
  };
  payables: {
    total: number;
    dueThisWeek: number;
    dueSoon: number;
    topBills: Array<{
      id: string;
      supplierName: string;
      billNumber: string;
      amount: number;
      dueDate: string;
    }>;
    /** Every seeded bill with its approval state — feeds Approval Management (BUG-28) */
    bills: DemoBill[];
  };
  company: {
    name: string;
    id: string;
  };
}

export interface DemoBill {
  id: string;
  billNumber: string;
  supplierName: string;
  amount: number;
  dueDate: string;
  status: string;
  approvalStatus: string;
}

// ---------------------------------------------------------------------------
// Cash position series — pure, deterministic, reproducible from seeded payments
// ---------------------------------------------------------------------------

const dayKey = (d: Date) => d.toISOString().slice(0, 10);
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function labelForDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return `${MONTHS_SHORT[m - 1]} ${d}`;
}

function bucketKey(key: string, granularity: 'weeks' | 'months' | 'quarters' | 'years'): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  if (granularity === 'weeks') {
    // ISO-ish week bucket: label by the Monday of the week
    const day = date.getUTCDay() || 7;
    const monday = new Date(date);
    monday.setUTCDate(date.getUTCDate() - day + 1);
    return dayKey(monday);
  }
  if (granularity === 'months') return `${y}-${String(m).padStart(2, '0')}`;
  if (granularity === 'quarters') return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
  return String(y);
}

function bucketLabel(key: string, granularity: 'weeks' | 'months' | 'quarters' | 'years'): string {
  if (granularity === 'weeks') return `Wk of ${labelForDay(key)}`;
  if (granularity === 'months') {
    const [y, m] = key.split('-').map(Number);
    return `${MONTHS_SHORT[m - 1]} '${String(y).slice(2)}`;
  }
  if (granularity === 'quarters') {
    const [y, q] = key.split('-Q');
    return `Q${q} '${y.slice(2)}`;
  }
  return key;
}

/**
 * Build the cash-position time series from seeded payments (BUG-24).
 *
 * Model (documented so every plotted point is reproducible by hand):
 * - Only `completed` payments count as cash movements.
 * - Daily net flow(d) = Σ received(d) − Σ sent(d).
 * - The series is anchored at TODAY = `anchorBalance` (the same
 *   `cashPosition.currentBalance` the dashboard KPI shows), and walks
 *   backward: position(end of previous day) = position(end of day) − netflow(day).
 * - weeks/months/quarters/years are aggregations of the SAME daily series:
 *   position = end-of-period daily position; moneyIn/Out = sums of the
 *   period's daily flows.
 */
export function buildCashPositionSeries(
  payments: DemoPayment[],
  anchorBalance: number,
  today: Date = new Date()
): CashPositionSeries {
  const completed = payments.filter((p) => p.status === 'completed');

  // Daily flows
  const flows = new Map<string, { moneyIn: number; moneyOut: number }>();
  for (const p of completed) {
    const key = p.date.slice(0, 10);
    const f = flows.get(key) || { moneyIn: 0, moneyOut: 0 };
    if (p.type === 'received') f.moneyIn += p.amount;
    else f.moneyOut += p.amount;
    flows.set(key, f);
  }

  const todayKey = dayKey(today);
  if (!flows.has(todayKey)) flows.set(todayKey, { moneyIn: 0, moneyOut: 0 });

  const keys = [...flows.keys()].sort();

  // Walk backward from the anchor (today's position)
  const positions = new Map<string, number>();
  let position = anchorBalance;
  for (let i = keys.length - 1; i >= 0; i--) {
    positions.set(keys[i], position);
    const f = flows.get(keys[i])!;
    position -= f.moneyIn - f.moneyOut; // position before this day's flows
  }

  const days: CashSeriesPoint[] = keys.map((k) => ({
    period: labelForDay(k),
    position: positions.get(k)!,
    moneyIn: flows.get(k)!.moneyIn,
    moneyOut: flows.get(k)!.moneyOut,
  }));

  const aggregate = (granularity: 'weeks' | 'months' | 'quarters' | 'years'): CashSeriesPoint[] => {
    const buckets = new Map<string, CashSeriesPoint>();
    for (const k of keys) {
      const bk = bucketKey(k, granularity);
      const existing = buckets.get(bk) || {
        period: bucketLabel(bk, granularity),
        position: 0,
        moneyIn: 0,
        moneyOut: 0,
      };
      existing.position = positions.get(k)!; // keys sorted asc -> ends at last day of bucket
      existing.moneyIn += flows.get(k)!.moneyIn;
      existing.moneyOut += flows.get(k)!.moneyOut;
      buckets.set(bk, existing);
    }
    return [...buckets.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([, v]) => v);
  };

  return {
    days,
    weeks: aggregate('weeks'),
    months: aggregate('months'),
    quarters: aggregate('quarters'),
    years: aggregate('years'),
  };
}

const daysAgo = (n: number) => {
  const d = new Date(Date.now() - n * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
};

/** Synthetic offline payments so the mock fallback exercises the same series builder. */
const MOCK_PAYMENTS: DemoPayment[] = [
  { id: 'mock-pay-1', referenceNumber: 'PMT-MOCK-001', date: daysAgo(28), amount: 120000, type: 'received', status: 'completed', description: 'Mock customer payment' },
  { id: 'mock-pay-2', referenceNumber: 'PMT-MOCK-002', date: daysAgo(21), amount: 64000, type: 'sent', status: 'completed', description: 'Mock supplier payment' },
  { id: 'mock-pay-3', referenceNumber: 'PMT-MOCK-003', date: daysAgo(14), amount: 95000, type: 'received', status: 'completed', description: 'Mock customer payment' },
  { id: 'mock-pay-4', referenceNumber: 'PMT-MOCK-004', date: daysAgo(7), amount: 41000, type: 'sent', status: 'completed', description: 'Mock supplier payment' },
  { id: 'mock-pay-5', referenceNumber: 'PMT-MOCK-005', date: daysAgo(2), amount: 88000, type: 'received', status: 'completed', description: 'Mock customer payment' },
];

function getMockDemoData(): DemoFinancialData {
  return {
    ...MOCK_DEMO_DATA_BASE,
    cashFlow: {
      payments: MOCK_PAYMENTS,
      series: buildCashPositionSeries(MOCK_PAYMENTS, MOCK_DEMO_DATA_BASE.cashPosition.currentBalance),
    },
    counts: {
      invoices: MOCK_DEMO_DATA_BASE.receivables.topInvoices.length,
      bills: MOCK_DEMO_DATA_BASE.payables.bills.length,
      payments: MOCK_PAYMENTS.length,
    },
  };
}

function mapApiPayments(rows: any[]): DemoPayment[] {
  return (rows || []).map((p) => ({
    id: p.id,
    referenceNumber: p.reference_number,
    date: typeof p.payment_date === 'string' ? p.payment_date.slice(0, 10) : dayKey(new Date(p.payment_date)),
    amount: Number(p.amount) || 0,
    type: p.payment_type === 'received' ? 'received' : 'sent',
    status: p.status,
    description: p.description || '',
  }));
}

/**
 * Fetch real financial data from the seeded database
 * Falls back to mock data if database is unreachable
 */
export async function fetchDemoFinancialData(userId?: string): Promise<DemoFinancialData | null> {
  try {
    console.log('[DemoDataService] Fetching demo financial data, userId:', userId);

    let companyId: string | null = null;
    let companyName = 'Summit Manufacturing Inc.';

    // Strategy 1: Use the current session's company via the Hono API
    try {
      const result = await apiClient.get<{ data: { id: string; name: string } }>('/companies/current');
      if (result?.data?.id) {
        companyId = result.data.id;
        companyName = result.data.name || 'Summit Manufacturing Inc.';
        console.log('[DemoDataService] Using current user company:', companyName, companyId);
      }
    } catch (dbError) {
      console.warn('[DemoDataService] /companies/current failed, falling back to mock data:', dbError);
      return getMockDemoData();
    }

    if (!companyId) {
      console.warn('[DemoDataService] No company found with seeded data, using mock data');
      return getMockDemoData();
    }

    // Fetch invoices, bills, and payments for the session's company via the
    // real Hono API (server scopes by session company through RLS middleware).
    // NOTE: the deprecated apiClient.from() shim silently returned empty data,
    // which is why every figure here used to be $0 (BUG-24 cluster root cause).
    let invoices: any[];
    let bills: any[];
    let payments: DemoPayment[];

    try {
      const invoiceResult = await apiClient.get<{ data: any[] | null; error: string | null }>('/invoices', { limit: '100' });
      if (invoiceResult.error) throw new Error(invoiceResult.error);
      invoices = (invoiceResult.data || []).map((inv) => ({
        ...inv,
        total_amount: Number(inv.total_amount) || 0,
        paid_amount: Number(inv.paid_amount) || 0,
      }));
      console.log('[DemoDataService] Fetched', invoices.length, 'invoices for company', companyId);
    } catch (dbError) {
      console.warn('[DemoDataService] Failed to fetch invoices, using mock data');
      return getMockDemoData();
    }

    try {
      const billResult = await apiClient.get<{ data: any[] | null; error: string | null }>('/bills', { limit: '100' });
      if (billResult.error) throw new Error(billResult.error);
      bills = (billResult.data || []).map((bill) => ({
        ...bill,
        total_amount: Number(bill.total_amount) || 0,
      }));
      console.log('[DemoDataService] Fetched', bills.length, 'bills for company', companyId);
    } catch (dbError) {
      console.warn('[DemoDataService] Failed to fetch bills, using mock data');
      return getMockDemoData();
    }

    try {
      const paymentResult = await apiClient.get<{ data: any[] | null; error: string | null }>('/payments');
      if (paymentResult.error) throw new Error(paymentResult.error);
      payments = mapApiPayments(paymentResult.data || []);
      console.log('[DemoDataService] Fetched', payments.length, 'payments for company', companyId);
    } catch (dbError) {
      console.warn('[DemoDataService] Failed to fetch payments, continuing without series');
      payments = [];
    }

    // Calculate receivables metrics
    const now = new Date();
    const totalReceivables = invoices?.reduce((sum, inv) => {
      const unpaid = inv.total_amount - (inv.paid_amount || 0);
      return sum + (inv.status !== 'paid' ? unpaid : 0);
    }, 0) || 0;

    const overdueInvoices = invoices?.filter(inv => {
      return inv.status !== 'paid' && new Date(inv.due_date) < now;
    }) || [];

    const totalOverdue = overdueInvoices.reduce((sum, inv) => {
      return sum + (inv.total_amount - (inv.paid_amount || 0));
    }, 0);

    // Aging buckets (BUG-113).
    //
    // There used to be four bucket NAMES but only three thresholds, so the
    // final `else` absorbed both 61-90 and 90+ and a 65-day invoice was
    // reported to a financial controller under "Antigüedad 90+ días". The
    // figure was right and only the label lied, which is why it survived every
    // screenshot review: the seeded tenants have an empty 61-90 band, so
    // nothing on screen ever looked wrong.
    //
    // Each branch below is the band its name claims, and each rendered label
    // states its range explicitly.
    const aging = {
      current: 0,
      thirtyDays: 0,  // 1-30
      sixtyDays: 0,   // 31-60
      ninetyDays: 0,  // 61-90
      ninetyPlus: 0   // 91+
    };

    invoices?.forEach(inv => {
      if (inv.status === 'paid') return;

      const dueDate = new Date(inv.due_date);
      const daysOld = Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
      const unpaid = inv.total_amount - (inv.paid_amount || 0);

      if (daysOld <= 0) {
        aging.current += unpaid;
      } else if (daysOld <= 30) {
        aging.thirtyDays += unpaid;
      } else if (daysOld <= 60) {
        aging.sixtyDays += unpaid;
      } else if (daysOld <= 90) {
        aging.ninetyDays += unpaid;
      } else {
        aging.ninetyPlus += unpaid;
      }
    });

    // Top overdue invoices — RANKED (BUG-112).
    //
    // This was `overdueInvoices.slice(0, 5)` over a list in whatever order
    // /invoices returned, which is issue-date order, not lateness order. The
    // answer headed "Principales Facturas Vencidas" therefore named whichever
    // rows came back first: on demo-mx it led with an invoice 18 days overdue
    // while omitting seven at 95-131 days.
    //
    // Sorted HERE, at the single producer, rather than in each consumer, so the
    // cash-flow answer and the receivables answer cannot drift apart. Ranked by
    // days overdue because that is what the label claims and what each rendered
    // row displays ("N días de vencimiento"); amount descending breaks ties so
    // the same data always yields the same three invoices in the same order.
    // Matches the pre-existing precedent in
    // AdvancedAnalyticsEngine.calculateReceivables.
    const outstanding = (inv: any) => inv.total_amount - (inv.paid_amount || 0);
    const daysPastDue = (inv: any) =>
      Math.floor((now.getTime() - new Date(inv.due_date).getTime()) / (1000 * 60 * 60 * 24));

    const topInvoices = [...overdueInvoices]
      .sort((a, b) => daysPastDue(b) - daysPastDue(a) || outstanding(b) - outstanding(a))
      .slice(0, 5)
      .map(inv => ({
        id: inv.id,
        buyerName: (inv.buyers as any)?.name || 'Unknown',
        invoiceNumber: inv.invoice_number,
        amount: outstanding(inv),
        dueDate: inv.due_date,
        daysOverdue: daysPastDue(inv)
      }));

    // Calculate payables metrics
    const totalPayables = bills?.reduce((sum, bill) => {
      return sum + (bill.status !== 'paid' ? bill.total_amount : 0);
    }, 0) || 0;

    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const dueThisWeek = bills?.filter(bill => {
      const dueDate = new Date(bill.due_date);
      return bill.status !== 'paid' && dueDate <= weekFromNow && dueDate >= now;
    }).reduce((sum, bill) => sum + bill.total_amount, 0) || 0;

    const twoWeeksFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    const dueSoon = bills?.filter(bill => {
      const dueDate = new Date(bill.due_date);
      return bill.status !== 'paid' && dueDate <= twoWeeksFromNow && dueDate > weekFromNow;
    }).reduce((sum, bill) => sum + bill.total_amount, 0) || 0;

    const topBills = bills?.slice(0, 5).map(bill => ({
      id: bill.id,
      supplierName: (bill.suppliers as any)?.name || 'Unknown',
      billNumber: bill.bill_number,
      amount: bill.total_amount,
      dueDate: bill.due_date
    })) || [];

    const allBills: DemoBill[] = (bills || []).map(bill => ({
      id: bill.id,
      billNumber: bill.bill_number,
      supplierName: (bill.suppliers as any)?.name || bill.supplier_name || 'Unknown',
      amount: bill.total_amount,
      dueDate: bill.due_date,
      status: bill.status || '',
      approvalStatus: bill.approval_status || ''
    }));

    return {
      cashPosition: {
        currentBalance: totalReceivables - totalPayables, // Simplified calculation
        totalReceivables,
        totalPayables
      },
      cashFlow: {
        payments,
        series: buildCashPositionSeries(payments, totalReceivables - totalPayables)
      },
      counts: {
        invoices: invoices.length,
        bills: bills.length,
        payments: payments.length
      },
      receivables: {
        total: totalReceivables,
        overdue: totalOverdue,
        aging,
        topInvoices
      },
      payables: {
        total: totalPayables,
        dueThisWeek,
        dueSoon,
        topBills,
        bills: allBills
      },
      company: {
        name: companyName,
        id: companyId
      }
    };
  } catch (error) {
    console.error('[DemoDataService] Error fetching demo financial data:', error);
    console.log('[DemoDataService] Returning mock data as fallback');
    return getMockDemoData();
  }
}

export const formatCurrency = (amount: number): string => {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
